import clsx, { ClassValue } from "clsx";
import React from "react";
import { useMediaQuery } from "react-responsive";
import { twMerge } from "tailwind-merge";
import { screens } from "tailwindcss/defaultTheme";

type SizeProps = { children?: React.ReactNode | any };

export const getIsDevelopment = () =>
  process.env.REACT_APP_NODE_ENV === "development";
export const getIsStaging = () =>
  process.env.REACT_APP_NODE_ENV === "staging";
export const getIsProduction = () =>
  process.env.REACT_APP_NODE_ENV === "production";

export const document = () => {
  if (typeof window === "undefined") return null;
  return window.document;
};

export const Desktop: React.FC<SizeProps> = props => {
  const { children } = props || {};
  const isDesktop = useMediaQuery({ minWidth: screens.md });

  return isDesktop ? children : null;
};

export const Tablet: React.FC<SizeProps> = props => {
  const { children } = props || {};
  const isTablet = useMediaQuery({
    minWidth: screens.sm,
    maxWidth: screens.md,
  });

  return isTablet ? children : null;
};

export const Mobile: React.FC<SizeProps> = props => {
  const { children } = props || {};
  const isMobile = useMediaQuery({ maxWidth: screens.md });

  return isMobile ? children : null;
};

export const cx = (...val: ClassValue[]) => twMerge(clsx(...val));

export const sleep = (ms = 0) =>
  new Promise(resolve => setTimeout(resolve, ms));

export const retry = async <T,>(
  fn: (args: { retries?: number }) => Promise<T> | T,
  { retries, retryIntervalMs }: { retries?: number; retryIntervalMs?: number },
): Promise<T> => {
  try {
    return await fn({ retries });
  } catch (error) {
    const hasRetryCount = retries !== undefined;

    if (hasRetryCount && retries <= 0) {
      throw error;
    }
    await sleep(retryIntervalMs);
    return retry(fn, {
      retries: hasRetryCount ? retries - 1 : undefined,
      retryIntervalMs: retryIntervalMs || 1000,
    });
  }
};

export function formatPhoneNumberLink(phoneNumber: string): string {
  const phoneRegEX = /\D/g;
  const formatted = phoneNumber?.replace(phoneRegEX, "");
  return "tel:+" + formatted;
}

export const formatPhoneNumber = (phoneNumberString: string) => {
  const cleaned = ("" + phoneNumberString).replace(/\D/g, "");
  const match = cleaned.match(/^(1|)?(\d{3})(\d{3})(\d{4})$/);
  if (match) {
    const intlCode = match[1] ? "+1" : "";
    return [intlCode, "-", match[2], "-", match[3], "-", match[4]].join("");
  }
  return null;
};

export const handleFormatPhoneNumber = (
  phoneNumber: string,
  oldPhoneNumber: string,
  onValueFormatted: (formattedValue: string) => void,
) => {
  let newPhoneNumber = phoneNumber;
  // Prevent formatting if user removes some of the input
  if (oldPhoneNumber.length < phoneNumber.length) {
    newPhoneNumber = newPhoneNumber.replace(/\D/g, "");
    newPhoneNumber = newPhoneNumber.replace(/^(\d{3})/, "($1) ");
    newPhoneNumber = newPhoneNumber.replace(/\s(\d{3})/, " $1-");
    newPhoneNumber = newPhoneNumber.replace(/(\d)-(\d{4}).*/, "$1-$2");
  }

  onValueFormatted(newPhoneNumber);
};

export function formatShownPhoneNumber(phoneNumber: string): string {
  const hyphenRegex = /-/g;

  if (phoneNumber[0] === "1") {
    // Remove the country code
    phoneNumber = phoneNumber.substring(1);
  }

  if (phoneNumber.includes("-")) {
    phoneNumber = phoneNumber.replace(hyphenRegex, "");
  }

  return `(${phoneNumber.substring(0, 3)}) ${phoneNumber.substring(
    3,
    6,
  )}-${phoneNumber.substring(6)}`;
}

export const getSingleQueryParam = (
  param: string | string[] | undefined | null,
): string => {
  if (param && Array.isArray(param)) return param[0];

  return param ? String(param) : "";
};

export const handleFormatPin = (
  pin: string,
  onValueFormatted: (formattedValue: string) => void,
) => {
  // Remove any non-digit characters
  let newPinValue = pin.replace(/\D/g, "");

  // Ensure the new pin value is not more than 4 digits
  if (newPinValue.length > 4) {
    newPinValue = newPinValue.slice(0, 4);
  }

  // Update the formatted value
  onValueFormatted(newPinValue);
};
